import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Calculator, ChevronRight, Plus, Trash2, FileText, Phone, MapPin, User, BarChart3, Info, Download, AlertTriangle, RefreshCw, Store, Camera } from 'lucide-react';
import StickyFooterNav from '../components/store/StickyFooterNav';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatPriceFixed, formatPriceInt, round2 } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Safe formula evaluator
function evalFormula(formula, values) {
  if (!formula) return 0;
  const expr = formula.replace(/\{([^}]+)\}/g, (_, name) => {
    const v = values[name];
    if (v === undefined || v === '' || v === null) return '0';
    const num = Number(v);
    return isNaN(num) ? '0' : String(num);
  });
  if (!/^[\d\s+\-*/().]+$/.test(expr.trim())) return 0;
  try { return new Function('return ' + expr.trim())(); } catch { return 0; }
}

// Detect conflicts between a saved quotation and the current template
function detectConflicts(q, tmpl) {
  if (!tmpl) return [];
  const conflicts = [];
  const fv = q.field_values || {};
  const fieldMap = {};
  for (const f of tmpl.fields) fieldMap[f.field_id] = f;

  // Check each saved value against current template
  for (const f of tmpl.fields) {
    if (f.type === 'dropdown' && fv[f.field_id]) {
      const savedLabel = fv[f.field_id];
      const optionExists = f.options?.some(o => o.label === savedLabel);
      if (!optionExists) {
        conflicts.push({ field_id: f.field_id, label: f.label, type: 'deleted_option', oldValue: savedLabel });
      }
    }
  }

  // Check if recomputed totals differ from stored (formula changes)
  const sorted = [...tmpl.fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const labelMap = {};
  let hiddenCalcSum = 0;
  for (const f of sorted) {
    if (f.type === 'calculated' && f.formula) {
      const formulaWithSum = f.formula.replace(/\{SUM_ALL\}/gi, String(hiddenCalcSum));
      const val = evalFormula(formulaWithSum, labelMap);
      const computed = round2(val);
      labelMap[f.label] = computed;
      if (!f.client_visible) hiddenCalcSum += computed;
      // Compare with stored value
      const stored = Number(fv[f.field_id] ?? 0);
      if (f.client_visible && Math.abs(computed - stored) > 0.01) {
        conflicts.push({ field_id: f.field_id, label: f.label, type: 'formula_changed', oldValue: stored, newValue: computed });
      }
    } else if (f.type === 'dropdown') {
      const sel = fv[f.field_id];
      const opt = f.options?.find(o => o.label === sel);
      labelMap[f.label] = opt?.value ?? 0;
    } else if (f.type === 'boolean') {
      labelMap[f.label] = fv[f.field_id] ? 1 : 0;
    } else {
      labelMap[f.label] = parseFloat(fv[f.field_id]) || 0;
    }
  }
  return conflicts;
}

// Recompute a quotation using current template (used when user clicks Recalculate)
function recomputeQuotation(q, tmpl) {
  if (!tmpl) return q;
  const sorted = [...tmpl.fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const labelMap = {};
  const newComputed = {};
  let hiddenCalcSum = 0;
  const fv = q.field_values || {};
  for (const f of sorted) {
    if (f.type === 'calculated' && f.formula) {
      const formulaWithSum = f.formula.replace(/\{SUM_ALL\}/gi, String(hiddenCalcSum));
      const val = evalFormula(formulaWithSum, labelMap);
      newComputed[f.field_id] = round2(val);
      labelMap[f.label] = newComputed[f.field_id];
      if (!f.client_visible) hiddenCalcSum += newComputed[f.field_id];
    } else if (f.type === 'dropdown') {
      const sel = fv[f.field_id];
      const opt = f.options?.find(o => o.label === sel);
      labelMap[f.label] = opt?.value ?? 0;
    } else if (f.type === 'boolean') {
      labelMap[f.label] = fv[f.field_id] ? 1 : 0;
    } else {
      labelMap[f.label] = parseFloat(fv[f.field_id]) || 0;
    }
  }
  return { ...q, field_values: { ...fv, ...newComputed } };
}

export default function ClientQuotation() {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noTemplate, setNoTemplate] = useState(false);

  // Admin mode (logged-in admin/staff acting on behalf of a client → bypass OTP)
  const [adminUser, setAdminUser] = useState(null); // { user_id, name, role } or null
  const [adminBootstrapping, setAdminBootstrapping] = useState(true);
  const isAdminMode = !!adminUser && (adminUser.role === 'admin' || adminUser.role === 'staff');

  // Lead capture
  const [lead, setLead] = useState(null);
  const [leadForm, setLeadForm] = useState({ name: '', mobile: '', city: '', pincode: '', address: '', budget: '', property_type: '' });
  const [submittingLead, setSubmittingLead] = useState(false);
  const [otp, setOtp] = useState({ sent: false, code: '', token: '', verified: false, sending: false, verifying: false, cooldown: 0 });

  // Existing quotations for this lead
  const [savedQuotations, setSavedQuotations] = useState([]);
  const [activeQuotationId, setActiveQuotationId] = useState(null); // null = new quote, id = editing existing
  const [quotationConflicts, setQuotationConflicts] = useState({}); // { quotation_id: [conflicts] }

  // Quotation form
  const [fieldValues, setFieldValues] = useState({});
  const [computedValues, setComputedValues] = useState({});
  const [fieldRemarks, setFieldRemarks] = useState({});
  const [miscExpenses, setMiscExpenses] = useState([]);

  // Room calculator
  const [calcOpen, setCalcOpen] = useState(false);
  const [calcTargetField, setCalcTargetField] = useState(null);
  const [roomEntries, setRoomEntries] = useState([]);
  const roomListRef = useRef(null);
  const newEntryIdRef = useRef(null);
  const [compareMode, setCompareMode] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/quotation-template`)
      .then(res => { setTemplate(res.data); setLoading(false); })
      .catch(() => { setNoTemplate(true); setLoading(false); });
  }, []);

  // Detect admin/staff session for OTP bypass
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then(res => { setAdminUser(res.data); })
      .catch(() => { setAdminUser(null); })
      .finally(() => setAdminBootstrapping(false));
  }, []);

  // Preload an existing lead via ?lead_id= (admin/staff entry from Leads page)
  useEffect(() => {
    if (!template || !adminUser || lead) return;
    const params = new URLSearchParams(window.location.search);
    const leadId = params.get('lead_id');
    if (!leadId) return;
    (async () => {
      try {
        // Find lead via admin-only listing
        const lRes = await axios.get(`${BACKEND_URL}/api/leads`, { withCredentials: true });
        const found = (lRes.data || []).find(l => l.lead_id === leadId);
        if (!found) {
          toast.error('Lead not found');
          return;
        }
        setLead(found);
        setLeadForm({
          name: found.name || '', mobile: found.mobile || '',
          city: found.city || '', pincode: found.pincode || '',
          address: found.address || '',
          budget: found.budget != null ? String(found.budget) : '',
          property_type: found.property_type || '',
        });
        const qRes = await axios.get(`${BACKEND_URL}/api/public/quotations/${leadId}`);
        const existing = qRes.data || [];
        setSavedQuotations(existing);
        const conflicts = {};
        for (const q of existing) {
          const c = detectConflicts(q, template);
          if (c.length > 0) conflicts[q.quotation_id] = c;
        }
        setQuotationConflicts(conflicts);
        if (existing.length > 0) {
          loadQuotation(existing[0]);
          toast.success(`Editing quotations for ${found.name} · ${existing.length} saved`);
        } else {
          setActiveQuotationId(null);
          toast.success(`Building new quotation for ${found.name}`);
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load lead');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, adminUser]);

  // Recompute calculated fields whenever inputs change
  const recompute = useCallback(() => {
    if (!template) return;
    const sorted = [...template.fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const labelMap = {};
    const newComputed = {};
    // Track sum of all hidden calculated fields for {SUM_ALL}
    let hiddenCalcSum = 0;
    for (const f of sorted) {
      if (f.type === 'calculated' && f.formula) {
        const formulaWithSum = f.formula.replace(/\{SUM_ALL\}/gi, String(hiddenCalcSum));
        const val = evalFormula(formulaWithSum, labelMap);
        newComputed[f.field_id] = round2(val);
        labelMap[f.label] = newComputed[f.field_id];
        if (!f.client_visible) hiddenCalcSum += newComputed[f.field_id];
      } else if (f.type === 'dropdown') {
        const sel = fieldValues[f.field_id];
        const opt = f.options?.find(o => o.label === sel);
        labelMap[f.label] = opt?.value ?? 0;
      } else if (f.type === 'boolean') {
        labelMap[f.label] = fieldValues[f.field_id] ? 1 : 0;
      } else {
        labelMap[f.label] = parseFloat(fieldValues[f.field_id]) || 0;
      }
    }
    setComputedValues(newComputed);
  }, [template, fieldValues]);

  useEffect(() => { recompute(); }, [recompute]);

  const updateField = (fieldId, value) => {
    setFieldValues(prev => ({ ...prev, [fieldId]: value }));
  };

  const updateRemark = (fieldId, value) => {
    const v = String(value || '').slice(0, 100);
    setFieldRemarks(prev => ({ ...prev, [fieldId]: v }));
  };

  const addMiscExpense = () => setMiscExpenses(prev => [...prev, { item_label: '', description: '', amount: '' }]);
  const updateMiscExpense = (idx, field, value) => {
    setMiscExpenses(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
  };
  const removeMiscExpense = (idx) => setMiscExpenses(prev => prev.filter((_, i) => i !== idx));
  const miscTotal = miscExpenses.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

  // OTP cooldown ticker
  useEffect(() => {
    if (otp.cooldown <= 0) return;
    const t = setTimeout(() => setOtp(p => ({ ...p, cooldown: Math.max(0, p.cooldown - 1) })), 1000);
    return () => clearTimeout(t);
  }, [otp.cooldown]);

  // Reset OTP state if user changes mobile
  useEffect(() => {
    setOtp(p => (p.sent || p.verified) ? { sent: false, code: '', token: '', verified: false, sending: false, verifying: false, cooldown: 0 } : p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadForm.mobile]);

  const sendOtp = async () => {
    const digits = leadForm.mobile.replace(/\D/g, '').replace(/^91/, '').replace(/^0/, '');
    if (digits.length !== 10 || !'6789'.includes(digits[0])) {
      toast.error('Enter a valid 10-digit Indian mobile number'); return;
    }
    setOtp(p => ({ ...p, sending: true }));
    try {
      const res = await axios.post(`${BACKEND_URL}/api/public/lead/send-otp`, { mobile: leadForm.mobile });
      setOtp(p => ({ ...p, sent: true, sending: false, cooldown: res.data?.cooldown_seconds || 30, code: '', verified: false, token: '' }));
      toast.success('OTP sent to your mobile');
    } catch (err) {
      setOtp(p => ({ ...p, sending: false }));
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    }
  };

  const verifyOtp = async () => {
    if (otp.code.replace(/\D/g, '').length < 4) { toast.error('Enter the OTP'); return; }
    setOtp(p => ({ ...p, verifying: true }));
    try {
      const res = await axios.post(`${BACKEND_URL}/api/public/lead/verify-otp`, { mobile: leadForm.mobile, code: otp.code });
      setOtp(p => ({ ...p, verifying: false, verified: true, token: res.data.token }));
      toast.success('Mobile verified');
    } catch (err) {
      setOtp(p => ({ ...p, verifying: false }));
      toast.error(err.response?.data?.detail || 'Invalid OTP');
    }
  };

  // Lead capture
  const submitLead = async (e) => {
    e.preventDefault();
    if (!leadForm.name || !leadForm.mobile || !leadForm.city || !leadForm.pincode || !leadForm.property_type) {
      toast.error('Please fill all required fields'); return;
    }
    if (!isAdminMode && (!otp.verified || !otp.token)) {
      toast.error('Please verify your mobile number first'); return;
    }
    setSubmittingLead(true);
    try {
      const payload = isAdminMode ? { ...leadForm } : { ...leadForm, otp_token: otp.token };
      const res = await axios.post(`${BACKEND_URL}/api/public/leads`, payload, { withCredentials: true });
      setLead(res.data);
      // Fetch existing quotations for this lead
      const qRes = await axios.get(`${BACKEND_URL}/api/public/quotations/${res.data.lead_id}`);
      const existing = qRes.data || [];
      setSavedQuotations(existing);
      // Detect conflicts for each quotation
      const conflicts = {};
      for (const q of existing) {
        const c = detectConflicts(q, template);
        if (c.length > 0) conflicts[q.quotation_id] = c;
      }
      setQuotationConflicts(conflicts);
      if (existing.length > 0) {
        // Load the first saved quotation for editing
        loadQuotation(existing[0]);
        toast.success(`Welcome back, ${res.data.name}! ${existing.length} saved quotation${existing.length > 1 ? 's' : ''} loaded.`);
      } else {
        setActiveQuotationId(null);
        toast.success(isAdminMode ? `Lead loaded — start building quotation` : 'Welcome! Start building your quotation.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally { setSubmittingLead(false); }
  };

  // Room calculator
  const openCalculator = (field) => {
    setCalcTargetField(field);
    setCalcOpen(true);
  };

  const addRoomEntry = (roomTypeId) => {
    const rt = template.room_types.find(r => r.room_type_id === roomTypeId);
    if (!rt) return;
    const inputVals = {};
    rt.inputs.forEach(inp => { inputVals[inp.key] = ''; });
    const newId = Date.now() + Math.random();
    newEntryIdRef.current = newId;
    setRoomEntries(prev => [...prev, { id: newId, room_type_id: roomTypeId, name: rt.name, inputValues: inputVals, result: 0 }]);
  };

  // Scroll to and focus the newly added room entry
  useEffect(() => {
    if (!newEntryIdRef.current) return;
    const id = newEntryIdRef.current;
    newEntryIdRef.current = null;
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="room-entry-${id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = el.querySelector('input[type="number"]');
        if (input) setTimeout(() => input.focus(), 300);
      }
    });
  }, [roomEntries]);

  const updateRoomEntry = (entryId, key, value) => {
    setRoomEntries(prev => prev.map(e => {
      if (e.id !== entryId) return e;
      const newInputs = { ...e.inputValues, [key]: value };
      const rt = template.room_types.find(r => r.room_type_id === e.room_type_id);
      const result = rt?.formula ? evalFormula(rt.formula, newInputs) : 0;
      return { ...e, inputValues: newInputs, result: round2(result) };
    }));
  };

  const removeRoomEntry = (entryId) => {
    setRoomEntries(prev => prev.filter(e => e.id !== entryId));
  };

  const confirmCalculator = () => {
    const total = roomEntries.reduce((s, e) => s + (e.result || 0), 0);
    if (calcTargetField) {
      updateField(calcTargetField.field_id, Math.ceil(total));
    }
    setCalcOpen(false);
  };

  const calcTotal = roomEntries.reduce((s, e) => s + (e.result || 0), 0);

  // Load a saved quotation into the form
  const loadQuotation = (q) => {
    setActiveQuotationId(q.quotation_id);
    setComputedValues({});
    // Separate input values from computed ones
    const inputVals = {};
    const templateFields = template?.fields || [];
    for (const f of templateFields) {
      if (f.type !== 'calculated') {
        const v = q.field_values?.[f.field_id];
        if (v !== undefined && v !== null) inputVals[f.field_id] = v;
      }
    }
    setFieldValues(inputVals);
    setFieldRemarks(q.field_remarks || {});
    setMiscExpenses((q.misc_expenses || []).map(m => ({ item_label: m.item_label || '', description: m.description || '', amount: m.amount ?? '' })));
    setRoomEntries((q.room_selections || []).map((rs, i) => ({
      id: Date.now() + i,
      room_type_id: rs.room_type_id,
      name: rs.name,
      inputValues: rs.inputs || {},
      result: rs.result || 0,
    })));
  };

  // Start a blank new quotation
  const startNewQuotation = () => {
    setActiveQuotationId(null);
    setFieldValues({});
    setComputedValues({});
    setFieldRemarks({});
    setMiscExpenses([]);
    setRoomEntries([]);
  };

  // Save quotation (create or update)
  const saveQuotation = async () => {
    const payload = {
      field_values: { ...fieldValues, ...computedValues },
      field_remarks: fieldRemarks,
      misc_expenses: miscExpenses
        .map(r => ({
          item_label: String(r.item_label || '').trim(),
          description: String(r.description || '').trim(),
          amount: parseFloat(r.amount) || 0
        }))
        .filter(r => r.item_label || r.description || r.amount > 0),
      room_selections: roomEntries.map(e => ({ room_type_id: e.room_type_id, name: e.name, inputs: e.inputValues, result: e.result })),
    };
    try {
      if (activeQuotationId) {
        // Update existing
        const res = await axios.put(`${BACKEND_URL}/api/public/quotations/${activeQuotationId}`, payload);
        setSavedQuotations(prev => prev.map(q => q.quotation_id === activeQuotationId ? res.data : q));
        // Clear conflicts for this quotation after save
        setQuotationConflicts(prev => { const c = { ...prev }; delete c[activeQuotationId]; return c; });
        toast.success('Quotation updated!');
      } else {
        // Create new
        const res = await axios.post(`${BACKEND_URL}/api/public/quotations`, {
          lead_id: lead.lead_id,
          template_id: template.template_id,
          ...payload,
        });
        const updated = [...savedQuotations, res.data];
        setSavedQuotations(updated);
        setActiveQuotationId(res.data.quotation_id);
        toast.success('Quotation saved!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save quotation');
    }
  };

  // Recalculate a specific quotation with the current template
  const recalculateQuotation = (qId) => {
    setSavedQuotations(prev => prev.map(q => {
      if (q.quotation_id !== qId) return q;
      // Clear deleted dropdown values, then recompute
      const cleaned = { ...q.field_values };
      for (const f of template.fields) {
        if (f.type === 'dropdown' && cleaned[f.field_id]) {
          const optionExists = f.options?.some(o => o.label === cleaned[f.field_id]);
          if (!optionExists) delete cleaned[f.field_id];
        }
      }
      return recomputeQuotation({ ...q, field_values: cleaned }, template);
    }));
    setQuotationConflicts(prev => { const c = { ...prev }; delete c[qId]; return c; });
    // If this is the active quotation, reload it into the form
    if (activeQuotationId === qId) {
      const updated = savedQuotations.find(q => q.quotation_id === qId);
      if (updated) {
        // Clean + recompute, then load
        const cleaned = { ...updated.field_values };
        for (const f of template.fields) {
          if (f.type === 'dropdown' && cleaned[f.field_id]) {
            const optionExists = f.options?.some(o => o.label === cleaned[f.field_id]);
            if (!optionExists) delete cleaned[f.field_id];
          }
        }
        const recomputed = recomputeQuotation({ ...updated, field_values: cleaned }, template);
        loadQuotation(recomputed);
      }
    }
    toast.success('Quotation recalculated with latest template');
  };

  // Format number as INR (always 2 decimals)
  const fmt = formatPriceFixed;
  // Format as INR with NO decimals — used for headline totals (Total Quotation Value, Per Sheet, Grand Total)
  const fmt0 = formatPriceInt;

  // PDF Generation — comparison-style layout
  const generatePDF = async () => {
    if (savedQuotations.length === 0) { toast.error('No saved quotations to export'); return; }
    try {
      const settingsRes = await axios.get(`${BACKEND_URL}/api/public/business-settings`);
      const biz = settingsRes.data;
      const qCount = savedQuotations.length;
      const isLandscape = qCount >= 3;
      const doc = new jsPDF({ orientation: isLandscape ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 14;
      let y = 12;

      // --- Header: Logo + Business Info ---
      const headerStartY = y;
      if (biz.logo_base64) {
        try {
          const logoW = 70; const logoH = 20;
          doc.addImage(biz.logo_base64, 'AUTO', margin, y, logoW, logoH);
          y = headerStartY + logoH + 3;
        } catch { /* skip logo */ }
      }
      if (!biz.logo_base64 || y === headerStartY) {
        doc.setFontSize(16); doc.setFont('helvetica', 'bold');
        doc.text(biz.business_name || 'Quotation', margin, y + 6);
        y = headerStartY + 10;
      }
      doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(100);
      if (biz.address) { doc.text(biz.address, margin, y); y += 4; }
      const contactParts = [biz.phone, biz.email].filter(Boolean).join('  |  ');
      if (contactParts) { doc.text(contactParts, margin, y); y += 4; }
      doc.setTextColor(0); y += 2;
      doc.setDrawColor(200); doc.setLineWidth(0.5); doc.line(margin, y, pageW - margin, y); y += 6;

      // --- Lead info ---
      doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.text('Prepared for:', margin, y);
      doc.setFont('helvetica', 'normal');
      doc.text(`${lead.name}  |  ${lead.mobile}  |  ${lead.city} - ${lead.pincode}`, margin + 24, y);
      doc.text(`Date: ${new Date().toLocaleDateString('en-IN')}`, pageW - margin, y, { align: 'right' });
      y += 8;

      // --- Helper: parse **bold** markers into segments ---
      const parseBold = (text) => {
        const parts = [];
        const boldRe = /\*\*(.+?)\*\*/g;
        let m, last = 0;
        while ((m = boldRe.exec(text)) !== null) {
          if (m.index > last) parts.push({ t: text.slice(last, m.index), b: false });
          parts.push({ t: m[1], b: true });
          last = m.index + m[0].length;
        }
        if (last < text.length) parts.push({ t: text.slice(last), b: false });
        if (parts.length === 0) parts.push({ t: text, b: false });
        return parts;
      };

      // --- Helper: render segments with word-wrap ---
      const renderWrapped = (segments, x, startY, maxW, fontSize, color, lineH) => {
        doc.setFontSize(fontSize); doc.setTextColor(...color);
        let cy = startY;
        let cx = x;
        const spaceW = (() => { doc.setFont('helvetica', 'normal'); return doc.getTextWidth(' '); })();

        for (const seg of segments) {
          doc.setFont('helvetica', seg.b ? 'bold' : 'normal');
          const words = seg.t.split(/(\s+)/);
          for (const word of words) {
            if (!word) continue;
            const ww = doc.getTextWidth(word);
            if (cx + ww > x + maxW && cx > x) {
              // Wrap to next line
              cy += lineH;
              cx = x;
              if (cy > pageH - 15) { doc.addPage(); cy = 14; }
            }
            doc.text(word, cx, cy);
            cx += ww;
          }
        }
        doc.setFont('helvetica', 'normal');
        return cy + lineH;
      };

      const drawMarkdownText = (text, startY, maxW) => {
        let cy = startY;
        const rawLines = text.split('\n');
        for (const rawLine of rawLines) {
          if (!rawLine.trim()) { cy += 3; continue; }
          if (cy > pageH - 15) { doc.addPage(); cy = 14; }

          if (rawLine.startsWith('# ')) {
            const content = rawLine.slice(2).replace(/\*\*/g, '');
            cy = renderWrapped([{ t: content, b: true }], margin, cy, maxW, 12, [40, 30, 20], 6);
          } else if (rawLine.startsWith('## ')) {
            const content = rawLine.slice(3).replace(/\*\*/g, '');
            cy = renderWrapped([{ t: content, b: true }], margin, cy, maxW, 10, [60, 50, 40], 5);
          } else if (rawLine.startsWith('- ')) {
            const content = rawLine.slice(2).trim();
            doc.setFontSize(8); doc.setTextColor(80, 80, 80);
            doc.text('\u2022', margin, cy);
            cy = renderWrapped(parseBold(content), margin + 5, cy, maxW - 5, 8, [80, 80, 80], 4);
          } else {
            cy = renderWrapped(parseBold(rawLine), margin, cy, maxW, 9, [60, 60, 60], 4);
          }
        }
        return cy;
      };

      // --- Greeting / Cover Note ---
      if (biz.greeting) {
        y = drawMarkdownText(biz.greeting, y, pageW - margin * 2);
        y += 3;
        doc.setTextColor(0);
      }

      const allVisible = template.fields.filter(f => f.client_visible).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const inputFieldDefs = allVisible.filter(f => f.type !== 'calculated');
      const summaryDefs = allVisible.filter(f => f.type === 'calculated');

      // Build a group map: for each dropdown (brand) field in a qty+brand group,
      // find its sibling number field. When that quantity is 0, the brand is
      // irrelevant and must be suppressed in the PDF (mirrors the input UI).
      const groupNumberFieldByDropdownId = {};
      {
        const byGroup = {};
        for (const f of inputFieldDefs) {
          const g = (f.group || '').trim();
          if (!g) continue;
          (byGroup[g] = byGroup[g] || []).push(f);
        }
        for (const fields of Object.values(byGroup)) {
          const numberField = fields.find(f => f.type === 'number');
          if (!numberField) continue;
          for (const f of fields) {
            if (f.type === 'dropdown') groupNumberFieldByDropdownId[f.field_id] = numberField.field_id;
          }
        }
      }

      const getDisplay = (q, f) => {
        // Suppress brand/option when its group's quantity is 0.
        const qtyFieldId = groupNumberFieldByDropdownId[f.field_id];
        if (qtyFieldId !== undefined) {
          const qty = parseFloat(q.field_values?.[qtyFieldId]) || 0;
          if (qty === 0) return '-';
        }
        const v = q.field_values?.[f.field_id];
        if (v === undefined || v === null || v === '') return '-';
        if (f.type === 'boolean') return v ? 'Yes' : 'No';
        return String(v);
      };

      const getCellWithRemark = (q, f) => {
        const base = getDisplay(q, f);
        const rem = q.field_remarks?.[f.field_id];
        if (f.enable_remarks && rem && rem.trim()) {
          return `${base}\nNote: ${rem.trim()}`;
        }
        return base;
      };

      // --- Build single comparison table (input fields + summary) ---
      const colCount = 1 + qCount;
      const head = [['', ...savedQuotations.map((q, i) => `Quotation ${i + 1}\n${q.created_at?.split('T')[0] || ''}`)]];

      // Input rows — only include fields that have at least one non-empty value (or any remark)
      const inputRows = [];
      for (const f of inputFieldDefs) {
        const vals = savedQuotations.map(q => getDisplay(q, f));
        if (vals.every(v => v === '-')) {
          // Even if all values blank, include the row if any quotation has a remark
          const anyRemark = f.enable_remarks && savedQuotations.some(q => (q.field_remarks?.[f.field_id] || '').trim());
          if (!anyRemark) continue;
        }
        const cellsWithRemarks = savedQuotations.map(q => getCellWithRemark(q, f));
        inputRows.push([f.label + (f.unit ? ` (${f.unit})` : ''), ...cellsWithRemarks]);
      }

      // Summary rows — first one (Total Quotation Value) is the headline; others are indicative
      const summaryRows = summaryDefs.map((f, idx) => {
        const isCurrency = f.unit === 'Rs' || f.unit === 'INR';
        const labelText = idx === 0 ? f.label : `${f.label}  (indicative)`;
        // Total Quotation Value (idx 0) + Per Sheet rows render without decimals
        const formatFn = isCurrency ? fmt0 : fmt;
        return [labelText, ...savedQuotations.map(q => {
          const v = q.field_values?.[f.field_id] ?? 0;
          return (isCurrency ? 'Rs. ' : '') + formatFn(v);
        })];
      });

      // Combine: inputs + divider + summary
      const allRows = [...inputRows];
      const summaryStartIdx = allRows.length;
      allRows.push(...summaryRows);

      const labelColWidth = isLandscape ? 70 : 65;

      autoTable(doc, {
        startY: y,
        head: head,
        body: allRows,
        margin: { left: margin, right: margin },
        styles: { fontSize: 8, cellPadding: 2.5, lineColor: [220, 220, 220] },
        headStyles: { fillColor: [77, 124, 15], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9, halign: 'center', cellPadding: 4 },
        columnStyles: { 0: { cellWidth: labelColWidth, fontStyle: 'bold', textColor: [60, 50, 40] } },
        theme: 'grid',
        didParseCell: (data) => {
          if (data.section !== 'body') return;
          const isSummaryRow = data.row.index >= summaryStartIdx;
          const isFirstSummary = data.row.index === summaryStartIdx;
          // Value columns right-aligned
          if (data.column.index > 0) {
            data.cell.styles.halign = 'center';
          }
          if (isSummaryRow) {
            data.cell.styles.fontStyle = 'bold';
            if (data.column.index > 0) data.cell.styles.halign = 'right';
          }
          // Non-headline summary rows (e.g. Per Sheet) — render lighter/italic to signal indicative
          if (isSummaryRow && data.row.index !== summaryStartIdx) {
            data.cell.styles.textColor = [130, 120, 100];
            data.cell.styles.fontStyle = 'italic';
            data.cell.styles.fillColor = [252, 250, 246];
          }
          if (isFirstSummary) {
            data.cell.styles.fillColor = [255, 240, 200];
            data.cell.styles.fontSize = 11;
            data.cell.styles.textColor = [120, 70, 0];
            data.cell.styles.cellPadding = 4;
          }
        },
        didDrawCell: (data) => {
          // Draw a thick amber line before the summary section
          if (data.section === 'body' && data.row.index === summaryStartIdx && data.column.index === 0) {
            doc.setDrawColor(200, 160, 60); doc.setLineWidth(0.8);
            doc.line(margin, data.cell.y, pageW - margin, data.cell.y);
          }
        },
      });
      y = doc.lastAutoTable.finalY + 8;

      // --- Project-specific (miscellaneous) expenses per quotation ---
      const anyMisc = savedQuotations.some(q => Array.isArray(q.misc_expenses) && q.misc_expenses.length > 0);
      if (anyMisc) {
        if (y > pageH - 30) { doc.addPage(); y = 14; }
        doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(40, 30, 20);
        doc.text('Project-specific Expenses', margin, y); y += 5;

        // For each quotation, render its own misc rows. Each item becomes TWO autoTable rows:
        //   Row A: [labelCell rowSpan=2,  amount per quote (bold)]
        //   Row B: [                       description per quote (italic, left)]
        // This lets us bold the amount, left-align descriptions, and keep amounts visually aligned.
        const miscBody = [];
        const miscMeta = []; // parallel array marking each row as 'amount' | 'desc' for styling
        const maxRows = Math.max(...savedQuotations.map(q => (q.misc_expenses || []).length));
        for (let i = 0; i < maxRows; i++) {
          const labels = savedQuotations.map(q => ((q.misc_expenses || [])[i]?.item_label || '').trim()).filter(Boolean);
          const descFallback = savedQuotations.map(q => ((q.misc_expenses || [])[i]?.description || '').trim()).find(Boolean);
          const rowLabel = labels[0] || descFallback || `Item ${i + 1}`;
          const allLabelsSame = labels.length > 0 && labels.every(l => l === labels[0]);

          // Row A — label (rowSpan 2) + amounts
          const amountRow = [{ content: rowLabel, rowSpan: 2, styles: { fontStyle: 'bold' } }];
          for (let c = 0; c < savedQuotations.length; c++) {
            const mi = (savedQuotations[c].misc_expenses || [])[i];
            amountRow.push(mi ? `Rs. ${fmt(mi.amount)}` : '-');
          }
          miscBody.push(amountRow);
          miscMeta.push('amount');

          // Row B — descriptions (per-quote-label optionally prepended)
          const descRow = [];
          for (let c = 0; c < savedQuotations.length; c++) {
            const mi = (savedQuotations[c].misc_expenses || [])[i];
            if (!mi) {
              descRow.push('');
              continue;
            }
            const showQuoteLabel = mi.item_label && (!allLabelsSame || labels.length === 0) && mi.item_label !== rowLabel;
            const parts = [];
            if (showQuoteLabel) parts.push(mi.item_label);
            if (mi.description) parts.push(mi.description);
            descRow.push(parts.join('\n'));
          }
          miscBody.push(descRow);
          miscMeta.push('desc');
        }
        // Misc total row
        const miscTotalRow = ['Miscellaneous Total', ...savedQuotations.map(q => {
          const t = (q.misc_expenses || []).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
          return `Rs. ${fmt(t)}`;
        })];
        // Grand total row (Total Quotation Value + Misc Total) — headline, no decimals
        const totalField = summaryDefs[0];
        const grandRow = ['Grand Total', ...savedQuotations.map(q => {
          const baseTotal = totalField ? (parseFloat(q.field_values?.[totalField.field_id]) || 0) : 0;
          const miscTot = (q.misc_expenses || []).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
          return `Rs. ${fmt0(baseTotal + miscTot)}`;
        })];

        autoTable(doc, {
          startY: y,
          head: [['', ...savedQuotations.map((q, i) => `Quotation ${i + 1}`)]],
          body: [...miscBody, miscTotalRow, grandRow],
          margin: { left: margin, right: margin },
          styles: { fontSize: 8, cellPadding: 2.5, lineColor: [220, 220, 220] },
          headStyles: { fillColor: [77, 124, 15], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9, halign: 'center', cellPadding: 4 },
          columnStyles: { 0: { cellWidth: labelColWidth, fontStyle: 'bold', textColor: [60, 50, 40] } },
          theme: 'grid',
          didParseCell: (data) => {
            if (data.section !== 'body') return;
            const rowIdx = data.row.index;
            const colIdx = data.column.index;
            const totalRowsCount = miscBody.length;
            const isMiscTotal = rowIdx === totalRowsCount;
            const isGrandTotal = rowIdx === totalRowsCount + 1;

            if (isMiscTotal) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [245, 240, 230];
              if (colIdx > 0) data.cell.styles.halign = 'right';
              return;
            }
            if (isGrandTotal) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [255, 220, 150];
              data.cell.styles.fontSize = 10;
              data.cell.styles.textColor = [120, 70, 0];
              if (colIdx > 0) data.cell.styles.halign = 'right';
              return;
            }
            // Misc body rows
            const kind = miscMeta[rowIdx];
            if (colIdx === 0) {
              data.cell.styles.halign = 'left';
              data.cell.styles.valign = 'middle';
              return;
            }
            if (kind === 'amount') {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.halign = 'center';
              data.cell.styles.fontSize = 9;
            } else {
              data.cell.styles.fontStyle = 'italic';
              data.cell.styles.halign = 'left';
              data.cell.styles.textColor = [110, 100, 85];
              data.cell.styles.fontSize = 7.5;
            }
          },
        });
        y = doc.lastAutoTable.finalY + 8;
      }

      // --- Remarks, Inclusions, Exclusions, Payment Terms ---
      const addTextSection = (title, text) => {
        if (!text) return;
        if (y > pageH - 25) { doc.addPage(); y = 14; }
        doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(40, 30, 20);
        doc.text(title, margin, y); y += 5;
        // Treat each line as a bullet if no markdown prefix present
        const normalized = text.split('\n').filter(Boolean).map(line => {
          if (line.startsWith('# ') || line.startsWith('## ') || line.startsWith('- ')) return line;
          return '- ' + line;
        }).join('\n');
        y = drawMarkdownText(normalized, y, pageW - margin * 2);
        y += 2;
      };
      if (biz.remarks) addTextSection('Remarks', biz.remarks);
      if (biz.inclusions) addTextSection('Inclusions', biz.inclusions);
      if (biz.exclusions) addTextSection('Exclusions unless specifically mentioned in the quotation', biz.exclusions);
      if (biz.payment_terms) addTextSection('Payment Terms', biz.payment_terms);

      // --- Closing Statement ---
      if (biz.footer) {
        if (y > pageH - 30) { doc.addPage(); y = 14; }
        doc.setDrawColor(200); doc.setLineWidth(0.3); doc.line(margin, y, pageW - margin, y); y += 5;
        y = drawMarkdownText(biz.footer, y, pageW - margin * 2);
        y += 3;
      }

      // --- Footer ---
      doc.setFontSize(7); doc.setTextColor(160);
      doc.text('This is a computer-generated quotation.', pageW / 2, pageH - 8, { align: 'center' });

      doc.save(`${lead.name.replace(/\s+/g, '_')}_${biz.business_name || 'Quotation'}.pdf`);
      toast.success('PDF downloaded!');
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate PDF');
    }
  };

  if (loading || adminBootstrapping) return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-700"></div>
    </div>
  );

  if (noTemplate) return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50">
      <div className="text-center max-w-md px-6">
        <FileText className="w-12 h-12 text-stone-300 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-stone-800 mb-2">Quotation Not Available</h1>
        <p className="text-sm text-stone-500">The quotation builder is not yet set up. Please contact us for a personalized quote.</p>
      </div>
    </div>
  );

  // LEAD CAPTURE GATE
  if (!lead) return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-100 flex flex-col">
      {!isAdminMode && <StickyFooterNav />}
      {!isAdminMode && (
        <div data-testid="getquote-topnav" className="w-full border-b border-stone-200 bg-white/60 backdrop-blur-md">
          <div className="max-w-5xl mx-auto px-4 h-12 flex items-center justify-between">
            <Link to="/" data-testid="getquote-brand" className="text-base font-semibold tracking-tight text-stone-900" style={{ fontFamily: '"Playfair Display", serif' }}>Nirmaan Store</Link>
            <nav className="flex items-center gap-3 text-sm">
              <Link to="/" data-testid="getquote-nav-shop" className="inline-flex items-center gap-1 text-stone-600 hover:text-amber-800 transition"><Store className="w-4 h-4" /> Shop</Link>
              <Link to="/portfolio" data-testid="getquote-nav-portfolio" className="inline-flex items-center gap-1 text-stone-600 hover:text-amber-800 transition"><Camera className="w-4 h-4" /> Portfolio</Link>
            </nav>
          </div>
        </div>
      )}
      <div className="flex-1 flex items-center sm:items-center justify-center px-3 py-4 sm:p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-5 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            {isAdminMode ? 'Build Quotation (Admin)' : 'Get Your Interior Quote'}
          </h1>
          <p className="text-xs sm:text-sm text-stone-500 mt-2 px-2">
            {isAdminMode
              ? `Hi ${adminUser?.name || 'Admin'} — enter the client's details to create or edit their quotation. OTP is not required.`
              : 'Enter your details to build a customized quotation for your interior work'}
          </p>
        </div>
        {isAdminMode && (
          <div data-testid="admin-mode-banner" className="mb-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-900 flex items-center gap-2">
            <span className="inline-block px-1.5 py-0.5 rounded bg-amber-700 text-white text-[9px] font-bold uppercase tracking-wide">Admin Mode</span>
            <span>Acting on behalf of client. Phone-verification step is skipped.</span>
          </div>
        )}
        <form onSubmit={submitLead} className="bg-white rounded-2xl shadow-lg border border-stone-200 p-4 sm:p-6 space-y-3 sm:space-y-4">
          <div>
            <Label className="text-xs font-medium text-stone-600 flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Full Name *</Label>
            <Input data-testid="lead-name" value={leadForm.name} onChange={e => setLeadForm(p => ({ ...p, name: e.target.value }))} placeholder="Your name" required className="mt-1 border-stone-200" />
          </div>

          {/* Mobile + Send OTP / Verified */}
          <div>
            <Label className="text-xs font-medium text-stone-600 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Mobile Number *</Label>
            <div className="mt-1 flex gap-2">
              <Input
                data-testid="lead-mobile"
                type="tel"
                value={leadForm.mobile}
                onChange={e => setLeadForm(p => ({ ...p, mobile: e.target.value }))}
                placeholder="10-digit mobile number"
                disabled={!isAdminMode && otp.verified}
                required
                className="border-stone-200 flex-1"
              />
              {!isAdminMode && !otp.verified && (
                <Button
                  data-testid="lead-send-otp-btn"
                  type="button"
                  onClick={sendOtp}
                  disabled={otp.sending || otp.cooldown > 0 || leadForm.mobile.replace(/\D/g, '').length < 10}
                  className="bg-stone-800 hover:bg-stone-900 text-white text-xs px-3 h-10 whitespace-nowrap"
                >
                  {otp.sending ? '...' : otp.cooldown > 0 ? `Resend ${otp.cooldown}s` : (otp.sent ? 'Resend OTP' : 'Send OTP')}
                </Button>
              )}
              {!isAdminMode && otp.verified && (
                <span data-testid="lead-mobile-verified" className="inline-flex items-center px-3 h-10 rounded-md bg-lime-50 border border-lime-200 text-xs font-semibold text-lime-700">
                  ✓ Verified
                </span>
              )}
            </div>

            {/* OTP input row — appears after Send OTP */}
            {!isAdminMode && otp.sent && !otp.verified && (
              <div data-testid="otp-row" className="mt-2 flex gap-2 items-center bg-amber-50/60 border border-amber-200 rounded-md px-3 py-2">
                <span className="text-[11px] text-amber-800 whitespace-nowrap">Enter OTP:</span>
                <Input
                  data-testid="lead-otp-input"
                  inputMode="numeric"
                  maxLength={8}
                  value={otp.code}
                  onChange={e => setOtp(p => ({ ...p, code: e.target.value.replace(/\D/g, '') }))}
                  placeholder="6-digit code"
                  className="border-amber-200 bg-white h-9 text-sm tracking-widest font-mono flex-1"
                />
                <Button
                  data-testid="lead-verify-otp-btn"
                  type="button"
                  size="sm"
                  onClick={verifyOtp}
                  disabled={otp.verifying || otp.code.length < 4}
                  className="bg-amber-700 hover:bg-amber-800 text-white text-xs h-9"
                >
                  {otp.verifying ? '...' : 'Verify'}
                </Button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-stone-600 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Town / City *</Label>
              <Input data-testid="lead-city" value={leadForm.city} onChange={e => setLeadForm(p => ({ ...p, city: e.target.value }))} placeholder="Your city" required className="mt-1 border-stone-200" />
            </div>
            <div>
              <Label className="text-xs font-medium text-stone-600">Pincode *</Label>
              <Input data-testid="lead-pincode" inputMode="numeric" pattern="[0-9]*" value={leadForm.pincode} onChange={e => setLeadForm(p => ({ ...p, pincode: e.target.value }))} placeholder="6-digit pincode" required className="mt-1 border-stone-200" />
            </div>
          </div>

          <div>
            <Label className="text-xs font-medium text-stone-600">Address (optional)</Label>
            <textarea
              data-testid="lead-address"
              value={leadForm.address}
              onChange={e => setLeadForm(p => ({ ...p, address: e.target.value }))}
              placeholder="Flat / House no, Building, Street, Landmark"
              maxLength={500}
              rows={2}
              className="mt-1 w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 leading-relaxed focus:outline-none focus:ring-2 focus:ring-amber-500/30"
            />
          </div>

          <div>
            <Label className="text-xs font-medium text-stone-600">Property Type *</Label>
            <Select value={leadForm.property_type} onValueChange={v => setLeadForm(p => ({ ...p, property_type: v }))}>
              <SelectTrigger data-testid="lead-property-type" className="mt-1 border-stone-200">
                <SelectValue placeholder="Select property type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Apartment / Flat" data-testid="property-apartment">Apartment / Flat</SelectItem>
                <SelectItem value="Independent / Individual House" data-testid="property-house">Independent / Individual House</SelectItem>
                <SelectItem value="Villa" data-testid="property-villa">Villa</SelectItem>
                <SelectItem value="Other" data-testid="property-other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-medium text-stone-600">Budget for your interiors (₹)</Label>
            <Input
              data-testid="lead-budget"
              type="number"
              min="0"
              step="any"
              value={leadForm.budget}
              onChange={e => setLeadForm(p => ({ ...p, budget: e.target.value }))}
              placeholder="e.g., 500000"
              className="mt-1 border-stone-200"
            />
            <p className="text-[10px] text-stone-400 mt-1">Optional — helps us recommend the right options for you.</p>
          </div>

          <Button
            data-testid="lead-submit-btn"
            type="submit"
            disabled={submittingLead || (!isAdminMode && !otp.verified)}
            className="w-full bg-amber-700 hover:bg-amber-800 text-white h-11 text-sm font-semibold disabled:opacity-50"
          >
            {submittingLead ? 'Please wait...' : (!isAdminMode && !otp.verified ? 'Verify mobile to continue' : (isAdminMode ? 'Continue to Quotation Builder' : 'Start My Quotation'))} <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
          <p className="text-[10px] text-stone-400 text-center">{isAdminMode ? 'Admin entry — no OTP sent to client.' : 'Your details are safe. We only use them to prepare your quotation.'}</p>
        </form>
      </div>
      </div>
    </div>
  );

  // Helper to get summary field values from a saved quotation
  const getSummaryVal = (q, fieldId) => {
    const v = q.field_values?.[fieldId];
    return v !== undefined ? Number(v) : 0;
  };

  // Get visible summary fields (calculated + client_visible)
  const summaryFieldDefs = template
    ? template.fields.filter(f => f.client_visible && f.type === 'calculated').sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : [];

  const getInputDisplay = (q, fieldId) => {
    const f = template?.fields?.find(ff => ff.field_id === fieldId);
    if (!f || !f.client_visible || f.type === 'calculated') return null;
    const v = q.field_values?.[fieldId];
    if (v === undefined || v === null || v === '') return null;
    if (f.type === 'boolean') return v ? 'Yes' : 'No';
    return String(v);
  };

  // COMPARE MODE
  if (lead && compareMode && savedQuotations.length >= 2) {
    const quotes = savedQuotations;
    const inputFieldDefs = template.fields
      .filter(f => f.client_visible && f.type !== 'calculated')
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-100">
        <div className={`mx-auto p-4 py-8 ${quotes.length >= 3 ? 'max-w-4xl' : 'max-w-3xl'}`}>
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-2xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Compare Your Quotations
              </h1>
              <p className="text-xs text-stone-500 mt-1">Hi {lead.name}, here are your {quotes.length} quotations side by side</p>
            </div>
            <Button data-testid="back-to-editor-btn" variant="outline" size="sm" onClick={() => setCompareMode(false)} className="text-xs border-stone-200">
              Back to Editor
            </Button>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-stone-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50">
                  <th className="text-left p-4 font-semibold text-stone-600"></th>
                  {quotes.map((q, idx) => (
                    <th key={q.quotation_id} className="text-center p-4 font-semibold text-stone-700">
                      <div className="text-xs uppercase tracking-wide text-amber-700">Quotation {idx + 1}</div>
                      <div className="text-[10px] text-stone-400 mt-0.5">{q.created_at?.split('T')[0]}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {inputFieldDefs.map(f => {
                  const vals = quotes.map(q => getInputDisplay(q, f.field_id));
                  if (vals.every(v => !v)) return null;
                  const allSame = vals.every(v => v === vals[0]);
                  return (
                    <tr key={f.field_id} className="border-b border-stone-100">
                      <td className="p-3 text-xs font-medium text-stone-600">{f.label}</td>
                      {vals.map((v, i) => (
                        <td key={i} className={`p-3 text-center text-xs tabular-nums ${!allSame ? 'bg-amber-50/50' : ''}`}>{v || '-'}</td>
                      ))}
                    </tr>
                  );
                })}
                <tr><td colSpan={1 + quotes.length} className="h-1 bg-amber-200"></td></tr>
                {summaryFieldDefs.map((f, idx) => {
                  const vals = quotes.map(q => getSummaryVal(q, f.field_id));
                  const isCurrency = f.unit === 'Rs' || f.unit === 'INR';
                  const isTotal = idx === 0;
                  const minVal = Math.min(...vals);
                  const allEqual = vals.every(v => v === minVal);
                  return (
                    <tr key={f.field_id} data-testid={`compare-${f.field_id}`} className={`border-b border-amber-100 ${isTotal ? 'bg-amber-50/60' : ''}`}>
                      <td className={`p-4 font-semibold ${isTotal ? 'text-sm text-stone-900' : 'text-xs text-stone-700'}`}>
                        {f.label}
                        {!isTotal && <span className="ml-1.5 text-[9px] uppercase tracking-wider text-stone-400 font-medium">(indicative)</span>}
                      </td>
                      {vals.map((v, i) => (
                        <td key={i} className={`p-4 text-center tabular-nums font-bold ${isTotal ? 'text-lg' : 'text-sm'} ${!isTotal ? 'text-stone-500 italic' : (v === minVal && !allEqual ? 'text-lime-700' : 'text-stone-900')}`}>
                          {isCurrency ? '\u20B9' : ''}{isCurrency ? fmt0(v) : fmt(v)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {(() => {
                  // Misc + Grand Total rows (only render if any quotation has misc)
                  const anyMisc = quotes.some(q => Array.isArray(q.misc_expenses) && q.misc_expenses.length > 0);
                  if (!anyMisc) return null;
                  const totalField = summaryFieldDefs[0];
                  const maxRows = Math.max(...quotes.map(q => (q.misc_expenses || []).length));
                  const miscRows = [];
                  for (let i = 0; i < maxRows; i++) {
                    const labels = quotes.map(q => ((q.misc_expenses || [])[i]?.item_label || '').trim()).filter(Boolean);
                    const descFallback = quotes.map(q => ((q.misc_expenses || [])[i]?.description || '').trim()).find(Boolean);
                    const rowLabel = labels[0] || descFallback || `Item ${i + 1}`;
                    const allLabelsSame = labels.length > 0 && labels.every(l => l === labels[0]);
                    miscRows.push(
                      <tr key={`misc-${i}`} className="border-b border-stone-100">
                        <td className="p-3 text-xs font-semibold text-stone-800 align-top">{rowLabel}</td>
                        {quotes.map((q, qi) => {
                          const m = (q.misc_expenses || [])[i];
                          if (!m) return <td key={qi} className="p-3 text-center text-xs text-stone-400 align-top">-</td>;
                          const showQuoteLabel = m.item_label && (!allLabelsSame || labels.length === 0) && m.item_label !== rowLabel;
                          return (
                            <td key={qi} className="p-3 text-xs tabular-nums align-top">
                              <div className="font-semibold text-stone-900 text-center mb-1">₹{fmt(m.amount)}</div>
                              {showQuoteLabel && (
                                <div className="text-stone-700 font-medium mb-0.5">{m.item_label}</div>
                              )}
                              {m.description && (
                                <div className="text-stone-500 italic whitespace-pre-wrap break-words leading-snug text-left">{m.description}</div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  }
                  const miscTotals = quotes.map(q => (q.misc_expenses || []).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0));
                  const grandTotals = quotes.map((q, qi) => {
                    const base = totalField ? (parseFloat(q.field_values?.[totalField.field_id]) || 0) : 0;
                    return base + miscTotals[qi];
                  });
                  const minGrand = Math.min(...grandTotals);
                  const allGrandEqual = grandTotals.every(g => g === minGrand);
                  return (
                    <>
                      <tr>
                        <td colSpan={1 + quotes.length} className="px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-stone-500 bg-stone-50 border-t border-stone-200">
                          Project-specific Expenses
                        </td>
                      </tr>
                      {miscRows}
                      <tr className="border-b border-stone-200 bg-stone-50">
                        <td className="p-3 text-xs font-semibold text-stone-700">Miscellaneous Total</td>
                        {miscTotals.map((m, i) => (
                          <td key={i} className="p-3 text-center text-sm tabular-nums font-semibold text-stone-900">₹{fmt(m)}</td>
                        ))}
                      </tr>
                      <tr data-testid="compare-grand-total" className="bg-amber-200/60 border-t-2 border-amber-400">
                        <td className="p-4 text-base font-bold text-stone-900">Grand Total</td>
                        {grandTotals.map((g, i) => (
                          <td key={i} className={`p-4 text-center tabular-nums font-bold text-xl ${g === minGrand && !allGrandEqual ? 'text-lime-700' : 'text-amber-900'}`}>₹{fmt0(g)}</td>
                        ))}
                      </tr>
                    </>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // MAIN QUOTATION VIEW — form + sidebar
  if (!lead) return null;
  const allVisible = template.fields.filter(f => f.client_visible).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const inputFields = allVisible.filter(f => f.type !== 'calculated');
  const summaryFields = allVisible.filter(f => f.type === 'calculated');
  const canCreateNew = savedQuotations.length < 3 && activeQuotationId !== null;
  const isEditing = activeQuotationId !== null;
  const hasAnyConflicts = Object.keys(quotationConflicts).length > 0;
  const activeConflicts = activeQuotationId ? (quotationConflicts[activeQuotationId] || []) : [];
  const conflictFieldIds = new Set(activeConflicts.map(c => c.field_id));

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-100">
      <div className="max-w-4xl mx-auto p-4 py-8 flex gap-5">

        {/* SIDEBAR — saved quotations */}
        <div className="w-56 flex-shrink-0">
          <div className="sticky top-8 space-y-3">
            <h3 className="text-xs font-bold text-stone-500 uppercase tracking-wide px-1">My Quotations</h3>

            {savedQuotations.map((q, idx) => {
              const isActive = activeQuotationId === q.quotation_id;
              const totalField = summaryFieldDefs[0];
              const storedVal = totalField ? getSummaryVal(q, totalField.field_id) : 0;
              const liveVal = totalField ? (computedValues[totalField.field_id] ?? 0) : 0;
              const baseTotal = isActive && liveVal > 0 ? liveVal : storedVal;
              const savedMisc = (q.misc_expenses || []).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
              const miscForThis = isActive ? miscTotal : savedMisc;
              const totalVal = (parseFloat(baseTotal) || 0) + miscForThis;
              const conflicts = quotationConflicts[q.quotation_id] || [];
              const hasConflict = conflicts.length > 0;
              return (
                <div key={q.quotation_id}>
                  <button
                    data-testid={`sidebar-quote-${idx + 1}`}
                    onClick={() => loadQuotation(q)}
                    className={`w-full text-left p-3 rounded-xl border transition-all ${hasConflict ? 'border-amber-400 bg-amber-50/60' : isActive ? 'bg-amber-50 border-amber-300 shadow-sm' : 'bg-white border-stone-200 hover:border-stone-300 hover:shadow-sm'}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-bold ${isActive ? 'text-amber-800' : 'text-stone-700'}`}>Quotation {idx + 1}</span>
                      <div className="flex items-center gap-1">
                        {hasConflict && <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />}
                        {isActive && !hasConflict && <span className="w-2 h-2 rounded-full bg-amber-500"></span>}
                      </div>
                    </div>
                    <div className="text-lg font-bold tabular-nums text-stone-900">
                      {totalVal > 0 ? `\u20B9${fmt(totalVal)}` : <span className="text-stone-400 text-xs">Not calculated</span>}
                    </div>
                    {miscForThis > 0 && (
                      <div className="text-[10px] text-stone-500 mt-0.5 tabular-nums">incl. misc ₹{fmt(miscForThis)}</div>
                    )}
                    <div className="text-[10px] text-stone-400 mt-0.5">{q.created_at?.split('T')[0]}</div>
                    {hasConflict && (
                      <div className="text-[10px] text-amber-700 mt-1 font-medium">Template changed</div>
                    )}
                  </button>
                  {hasConflict && (
                    <button
                      data-testid={`recalculate-quote-${idx + 1}`}
                      onClick={(e) => { e.stopPropagation(); recalculateQuotation(q.quotation_id); }}
                      className="w-full mt-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-100 text-amber-800 text-[11px] font-semibold hover:bg-amber-200 transition-colors"
                    >
                      <RefreshCw className="w-3 h-3" /> Recalculate
                    </button>
                  )}
                </div>
              );
            })}

            {/* New quotation button */}
            {savedQuotations.length < 3 && (
              <button
                data-testid="sidebar-new-quote"
                onClick={startNewQuotation}
                className={`w-full text-left p-3 rounded-xl border border-dashed transition-all ${activeQuotationId === null ? 'bg-amber-50 border-amber-300' : 'border-stone-300 hover:border-amber-300 hover:bg-amber-50/30'}`}
              >
                <div className="flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5 text-amber-700" />
                  <span className="text-xs font-semibold text-amber-800">
                    {savedQuotations.length === 0 ? 'New Quotation' : `Quotation ${savedQuotations.length + 1}`}
                  </span>
                </div>
                {activeQuotationId === null && <div className="text-[10px] text-amber-600 mt-1">Currently editing</div>}
              </button>
            )}

            {/* Compare button */}
            {savedQuotations.length >= 2 && (
              <Button
                data-testid="compare-quotes-btn"
                variant="outline"
                size="sm"
                onClick={() => setCompareMode(true)}
                disabled={hasAnyConflicts}
                className={`w-full text-xs ${hasAnyConflicts ? 'opacity-50 cursor-not-allowed border-stone-200 text-stone-400' : 'border-amber-200 text-amber-800 hover:bg-amber-50'}`}
                title={hasAnyConflicts ? 'Recalculate all quotations first' : ''}
              >
                <BarChart3 className="w-3.5 h-3.5 mr-1" /> Compare Side by Side
              </Button>
            )}

            {/* Download PDF button */}
            {savedQuotations.length > 0 && (
              <Button
                data-testid="download-pdf-btn"
                variant="outline"
                size="sm"
                onClick={generatePDF}
                disabled={hasAnyConflicts}
                className={`w-full text-xs ${hasAnyConflicts ? 'opacity-50 cursor-not-allowed border-stone-200 text-stone-400' : 'border-stone-200 text-stone-700 hover:bg-stone-50'}`}
                title={hasAnyConflicts ? 'Recalculate all quotations first' : ''}
              >
                <Download className="w-3.5 h-3.5 mr-1" /> Download PDF
              </Button>
            )}

            {/* Conflict info */}
            {hasAnyConflicts && (
              <div data-testid="conflict-notice" className="px-2 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[10px] text-amber-800 leading-relaxed">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                Template has been updated. Recalculate marked quotations to compare or download PDF.
              </div>
            )}
          </div>
        </div>

        {/* FORM */}
        <div className="flex-1 min-w-0">
          <div className="mb-5">
            <h1 className="text-2xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {template.name}
            </h1>
            <p className="text-xs text-stone-500 mt-1">
              {isEditing
                ? `Editing Quotation ${savedQuotations.findIndex(q => q.quotation_id === activeQuotationId) + 1} — change any values and save`
                : `Hi ${lead.name}, fill in the details below to get your estimate`
              }
            </p>
          </div>

          {/* Conflict banner */}
          {activeConflicts.length > 0 && (
            <div data-testid="conflict-banner" className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-300 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-800 leading-relaxed">
                <span className="font-semibold">Template has changed</span> since this quotation was created.
                {activeConflicts.some(c => c.type === 'deleted_option') && (
                  <span> Some dropdown selections are no longer available (shown with <span className="line-through">strikethrough</span>).</span>
                )}
                {activeConflicts.some(c => c.type === 'formula_changed') && (
                  <span> Calculated values may differ from the original quotation.</span>
                )}
                <span> Click <strong>Recalculate</strong> in the sidebar to update.</span>
              </div>
            </div>
          )}

        {/* CLIENT INPUT FIELDS — grouped */}
        <div className="bg-white rounded-2xl shadow-lg border border-stone-200 overflow-hidden">
          <GroupedInputFields
            inputFields={inputFields}
            fieldValues={fieldValues}
            updateField={updateField}
            openCalculator={openCalculator}
            conflictFieldIds={conflictFieldIds}
            conflicts={activeConflicts}
            fieldRemarks={fieldRemarks}
            updateRemark={updateRemark}
          />
        </div>

        {/* MISCELLANEOUS PROJECT EXPENSES */}
        <div className="mt-4 bg-white rounded-2xl shadow-sm border-2 border-stone-200 overflow-hidden">
          <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
            <div>
              <h3 className="text-xs font-bold text-stone-700 uppercase tracking-wide">Project-specific Expenses</h3>
              <p className="text-[10px] text-stone-500 mt-0.5">Electrical, plumbing, false ceiling etc. — added separately to the final total. Not included in per-sheet calculation.</p>
            </div>
            <Button data-testid="add-misc-btn" type="button" onClick={addMiscExpense} size="sm" variant="outline" className="text-xs border-amber-200 text-amber-800 hover:bg-amber-50">
              <Plus className="w-3 h-3 mr-1" /> Add Expense
            </Button>
          </div>
          <div className="divide-y divide-stone-100">
            {miscExpenses.length === 0 ? (
              <div className="px-5 py-6 text-center text-xs text-stone-400">No project-specific expenses yet. Click "Add Expense" to add one.</div>
            ) : miscExpenses.map((row, idx) => (
              <div key={idx} data-testid={`misc-row-${idx}`} className="px-5 py-3 space-y-2">
                <div className="grid grid-cols-[1fr_auto_auto] items-center gap-3">
                  <Input
                    data-testid={`misc-label-${idx}`}
                    value={row.item_label || ''}
                    onChange={(e) => updateMiscExpense(idx, 'item_label', e.target.value)}
                    placeholder="Item label (e.g. Electrical Work, Plumbing)"
                    maxLength={200}
                    className="border-stone-200 text-sm h-9 font-medium"
                  />
                  <div className="w-32">
                    <Input
                      data-testid={`misc-amt-${idx}`}
                      type="number" step="any" min="0"
                      value={row.amount}
                      onChange={(e) => updateMiscExpense(idx, 'amount', e.target.value)}
                      placeholder="Amount ₹"
                      className="w-full border-stone-200 text-sm text-right tabular-nums h-9"
                    />
                  </div>
                  <Button data-testid={`misc-remove-${idx}`} type="button" variant="ghost" size="icon" className="h-8 w-8 text-stone-400 hover:text-red-600" onClick={() => removeMiscExpense(idx)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <textarea
                  data-testid={`misc-desc-${idx}`}
                  value={row.description || ''}
                  onChange={(e) => updateMiscExpense(idx, 'description', e.target.value)}
                  placeholder="Description (optional) — scope, brand notes, what's included…"
                  maxLength={1000}
                  rows={3}
                  className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-xs text-stone-700 leading-relaxed resize-y min-h-[64px] focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                />
              </div>
            ))}
            {miscExpenses.length > 0 && (
              <div className="flex items-center justify-between px-5 py-3 bg-stone-50 font-semibold">
                <span className="text-sm text-stone-700">Misc Total</span>
                <span data-testid="misc-total" className="text-sm tabular-nums text-stone-900">₹{fmt(miscTotal)}</span>
              </div>
            )}
          </div>
        </div>

        {/* SUMMARY — visible calculated fields */}
        {summaryFields.length > 0 && (
          <div className="mt-4 bg-gradient-to-br from-amber-50 to-stone-50 rounded-2xl border-2 border-amber-200 overflow-hidden">
            <div className="px-5 py-3 bg-amber-100/60 border-b border-amber-200">
              <h3 className="text-xs font-bold text-amber-900 uppercase tracking-wide">Your Estimate</h3>
            </div>
            <div className="divide-y divide-amber-100">
              {summaryFields.map((f, idx) => {
                const val = computedValues[f.field_id] ?? 0;
                const isTotal = idx === 0;
                const isCurrency = f.unit === 'Rs' || f.unit === 'INR';
                return (
                  <div key={f.field_id} data-testid={`quote-field-${f.field_id}`}
                    className={`flex items-center justify-between px-5 ${isTotal ? 'py-5 bg-amber-50/40' : 'py-3.5'}`}
                  >
                    <span className={`font-medium ${isTotal ? 'text-base text-stone-900' : 'text-sm text-stone-600 italic'}`}>
                      {f.label}
                      {!isTotal && <span className="ml-1.5 text-[9px] uppercase tracking-wider text-stone-400 not-italic font-medium">(indicative · not in total)</span>}
                    </span>
                    <span data-testid={`quote-calc-${f.field_id}`}
                      className={`tabular-nums font-bold ${isTotal ? 'text-xl text-amber-800' : 'text-base text-stone-500 italic'}`}
                    >
                      {isCurrency ? '\u20B9' : ''}{isCurrency ? fmt0(val) : fmt(val)}
                      {f.unit && !isCurrency && <span className="text-xs font-normal text-stone-500 ml-1">{f.unit}</span>}
                    </span>
                  </div>
                );
              })}
              {miscTotal > 0 && (() => {
                const totalField = summaryFields[0];
                const baseTotal = totalField ? (computedValues[totalField.field_id] ?? 0) : 0;
                const grandTotal = (parseFloat(baseTotal) || 0) + miscTotal;
                return (
                  <>
                    <div className="flex items-center justify-between px-5 py-3 bg-white/40">
                      <span className="text-sm font-medium text-stone-700">+ Miscellaneous Expenses</span>
                      <span data-testid="quote-misc-summary" className="tabular-nums font-semibold text-base text-stone-900">₹{fmt(miscTotal)}</span>
                    </div>
                    <div className="flex items-center justify-between px-5 py-5 bg-amber-200/50 border-t-2 border-amber-300">
                      <span className="text-base font-bold text-stone-900">Grand Total</span>
                      <span data-testid="quote-grand-total" className="tabular-nums font-bold text-2xl text-amber-900">₹{fmt0(grandTotal)}</span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Empty dropdown warning */}
        {(() => {
          const emptyDropdowns = inputFields.filter(f => {
            if (f.type !== 'dropdown') return false;
            // For grouped fields, only warn if the number field has a value > 0
            if (f.group?.trim()) {
              const groupFields = inputFields.filter(gf => gf.group?.trim() === f.group?.trim());
              const numField = groupFields.find(gf => gf.type === 'number');
              if (numField && (parseFloat(fieldValues[numField.field_id]) || 0) === 0) return false;
            }
            return !fieldValues[f.field_id];
          });
          if (emptyDropdowns.length === 0) return null;
          return (
            <div data-testid="empty-dropdown-warning" className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-800 leading-relaxed">
                <span className="font-semibold">{emptyDropdowns.length} dropdown{emptyDropdowns.length > 1 ? 's' : ''} not selected</span> — the estimate may be lower than actual.
                <div className="mt-1 text-[10px] text-amber-600">
                  {emptyDropdowns.map(f => f.group?.trim() || f.label).join(', ')}
                </div>
              </div>
            </div>
          );
        })()}

        <div className="mt-6 flex justify-end">
          <Button data-testid="save-quotation-btn" onClick={saveQuotation} size="lg" className="bg-amber-700 hover:bg-amber-800 text-white px-8 h-11 text-sm font-semibold shadow-md">
            <FileText className="w-4 h-4 mr-2" /> {isEditing ? 'Save Changes' : 'Save Quotation'}
          </Button>
        </div>
      </div>
      </div>

      {/* ROOM CALCULATOR DIALOG */}
      <Dialog open={calcOpen} onOpenChange={setCalcOpen}>
        <DialogContent className="bg-white max-w-xl max-h-[85vh] flex flex-col p-0">
          <div className="px-6 pt-6 pb-3 border-b border-stone-100">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Calculator className="w-5 h-5 text-amber-700" /> Room Calculator</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-stone-500 mt-1">Add all rooms/items in your house, enter dimensions, and we'll estimate the total.</p>
          </div>

          {/* Scrollable room entries */}
          <div className="flex-1 overflow-y-auto px-6 py-3 space-y-2">
            {roomEntries.length === 0 && (
              <div className="text-center py-8 text-stone-400 text-xs">No rooms added yet. Use the button below to add rooms.</div>
            )}
            {roomEntries.map((entry) => {
              const rt = template.room_types.find(r => r.room_type_id === entry.room_type_id);
              return (
                <div key={entry.id} data-testid={`room-entry-${entry.id}`} className="bg-stone-50 rounded-lg border border-stone-200 px-3 py-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <div>
                      <span className="text-xs font-semibold text-stone-700">{entry.name}</span>
                      {rt?.remark && <p className="text-[10px] text-stone-400 mt-0.5">{rt.remark}</p>}
                    </div>
                    <button onClick={() => removeRoomEntry(entry.id)} className="text-stone-400 hover:text-red-600 transition-colors p-0.5" title="Remove">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {rt?.inputs.map(inp => (
                      <div key={inp.key} className="flex-1 min-w-[90px]">
                        <Label className="text-[10px] text-stone-500">{inp.label}</Label>
                        {inp.type === 'dropdown' && inp.options?.length > 0 ? (
                          <Select value={entry.inputValues[inp.key] || ''} onValueChange={v => updateRoomEntry(entry.id, inp.key, v)}>
                            <SelectTrigger className="border-stone-200 text-xs h-8 mt-0.5"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>{inp.options.map(o => <SelectItem key={o.label || o} value={o.value?.toString() || o.label || o}>{o.label || o}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : (
                          <Input
                            type="number"
                            step="any"
                            value={entry.inputValues[inp.key] ?? ''}
                            onChange={e => updateRoomEntry(entry.id, inp.key, e.target.value)}
                            placeholder="0"
                            className="border-stone-200 text-xs h-8 mt-0.5"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Sticky bottom: Add button + total + confirm */}
          <div className="px-6 pb-5 pt-3 border-t border-stone-200 bg-white space-y-3">
            {template.room_types?.length > 0 && (
              <Select onValueChange={addRoomEntry} value="">
                <SelectTrigger data-testid="room-type-selector" className="border-dashed border-amber-300 bg-amber-50/50 text-sm text-amber-800 font-medium hover:bg-amber-50 transition-colors">
                  <div className="flex items-center gap-1.5">
                    <Plus className="w-4 h-4" />
                    <span>Add a Room / Item</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {template.room_types.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map(rt => (
                    <SelectItem key={rt.room_type_id} value={rt.room_type_id}>{rt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Button data-testid="confirm-calculator-btn" onClick={confirmCalculator} className="w-full bg-amber-700 hover:bg-amber-800 text-white h-11 text-sm font-semibold">
              Use Total: {Math.ceil(calcTotal)} sheets
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Render a single field input ---
function FieldInput({ f, fieldValues, updateField, openCalculator, disabled }) {
  if (f.type === 'number') {
    return (
      <div className="flex items-center gap-2">
        <Input
          data-testid={`quote-input-${f.field_id}`}
          type="number"
          step="any"
          value={fieldValues[f.field_id] ?? ''}
          onChange={e => updateField(f.field_id, e.target.value)}
          placeholder="0"
          disabled={disabled}
          className={`w-28 border-stone-200 text-sm text-right tabular-nums ${disabled ? 'opacity-40' : ''}`}
        />
        {f.has_helper && (
          <Button
            data-testid={`calc-helper-${f.field_id}`}
            type="button" size="sm" variant="outline"
            onClick={() => openCalculator(f)}
            className="text-xs h-8 border-amber-200 text-amber-800 hover:bg-amber-50"
          >
            <Calculator className="w-3.5 h-3.5 mr-1" /> Calculate
          </Button>
        )}
      </div>
    );
  }
  if (f.type === 'dropdown') {
    return (
      <Select value={fieldValues[f.field_id] || ''} onValueChange={v => updateField(f.field_id, v)} disabled={disabled}>
        <SelectTrigger data-testid={`quote-select-${f.field_id}`} className={`w-44 border-stone-200 text-sm ${disabled ? 'opacity-40' : ''}`}>
          <SelectValue placeholder="Select..." />
        </SelectTrigger>
        <SelectContent>
          {f.options?.map(o => (
            <SelectItem key={o.label} value={o.label}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (f.type === 'boolean') {
    return (
      <Switch
        data-testid={`quote-bool-${f.field_id}`}
        checked={!!fieldValues[f.field_id]}
        onCheckedChange={v => updateField(f.field_id, v)}
        disabled={disabled}
      />
    );
  }
  return null;
}

// --- Remark display: hint text + info tooltip ---
function RemarkDisplay({ remark }) {
  const [showTip, setShowTip] = useState(false);
  if (!remark) return null;
  return (
    <span className="relative inline-flex items-center ml-1.5">
      <button type="button" className="text-stone-400 hover:text-amber-700 transition-colors"
        onMouseEnter={() => setShowTip(true)} onMouseLeave={() => setShowTip(false)}
        onClick={() => setShowTip(p => !p)}>
        <Info className="w-3.5 h-3.5" />
      </button>
      {showTip && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-56 px-3 py-2 bg-stone-800 text-white text-[11px] leading-relaxed rounded-lg shadow-lg">
          {remark}
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-stone-800 rotate-45 -mt-1"></div>
        </div>
      )}
    </span>
  );
}

// --- Group fields by group name, render with consistent two-column layout ---
function RemarkInput({ fieldId, fieldRemarks, updateRemark, dataTestId }) {
  const val = fieldRemarks[fieldId] || '';
  return (
    <div className="px-5 pb-3 -mt-2 border-b border-stone-100 last:border-b-0">
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-stone-500 uppercase tracking-wider font-semibold">Remarks</span>
        <Input
          data-testid={dataTestId}
          value={val}
          onChange={e => updateRemark(fieldId, e.target.value)}
          maxLength={100}
          placeholder="Optional note for this line (shown on the PDF)"
          className="h-7 text-xs border-stone-200 flex-1"
        />
        <span className="text-[10px] text-stone-400 tabular-nums w-10 text-right">{val.length}/100</span>
      </div>
    </div>
  );
}

function GroupedInputFields({ inputFields, fieldValues, updateField, openCalculator, conflictFieldIds = new Set(), conflicts = [], fieldRemarks = {}, updateRemark = () => {} }) {
  const rendered = [];
  const seenGroups = new Set();
  const conflictMap = {};
  for (const c of conflicts) { if (c.type === 'deleted_option') conflictMap[c.field_id] = c; }

  for (const f of inputFields) {
    const group = f.group?.trim();
    const hasConflict = conflictFieldIds.has(f.field_id) && conflictMap[f.field_id];
    if (!group) {
      // Ungrouped — render in the standard two-slot layout
      const isNumber = f.type === 'number';
      const isDropdown = f.type === 'dropdown';
      const isBool = f.type === 'boolean';
      rendered.push(
        <React.Fragment key={f.field_id}>
        <div data-testid={`quote-field-${f.field_id}`}
          className={`grid grid-cols-[1fr_auto_auto] items-center gap-3 px-5 py-3.5 ${f.enable_remarks ? '' : 'border-b border-stone-100 last:border-b-0'} ${hasConflict ? 'bg-red-50/50' : ''}`}>
          <div className="min-w-0">
            <div className="flex items-center">
              <label className="text-sm font-medium text-stone-800">
                {f.label} {f.required && <span className="text-red-500">*</span>}
                {f.unit && <span className="text-xs text-stone-400 ml-1">({f.unit})</span>}
              </label>
              <RemarkDisplay remark={f.remark} />
            </div>
            {hasConflict && (
              <p className="text-[10px] text-red-600 mt-0.5 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                <span className="line-through">{conflictMap[f.field_id].oldValue}</span>
                <span>is no longer available</span>
              </p>
            )}
            {!hasConflict && f.remark && <p className="text-[10px] text-stone-400 mt-0.5">{f.remark}</p>}
          </div>
          {/* Number slot */}
          <div className="w-28">
            {isNumber ? (
              <Input
                data-testid={`quote-input-${f.field_id}`}
                type="number" step="any"
                value={fieldValues[f.field_id] ?? ''}
                onChange={e => updateField(f.field_id, e.target.value)}
                placeholder="0"
                className="w-full border-stone-200 text-sm text-right tabular-nums"
              />
            ) : isBool ? (
              <div className="flex justify-end">
                <Switch
                  data-testid={`quote-bool-${f.field_id}`}
                  checked={!!fieldValues[f.field_id]}
                  onCheckedChange={v => updateField(f.field_id, v)}
                />
              </div>
            ) : <div />}
          </div>
          {/* Brand/dropdown slot */}
          <div className="w-48">
            {isDropdown ? (
              <Select value={fieldValues[f.field_id] || ''} onValueChange={v => updateField(f.field_id, v)}>
                <SelectTrigger data-testid={`quote-select-${f.field_id}`} className={`w-full text-sm ${hasConflict ? 'border-red-300 bg-red-50' : 'border-stone-200'}`}>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {f.options?.map(o => <SelectItem key={o.label} value={o.label}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : f.has_helper ? (
              <Button
                data-testid={`calc-helper-${f.field_id}`}
                type="button" size="sm" variant="outline"
                onClick={() => openCalculator(f)}
                className="text-xs h-9 w-full border-amber-200 text-amber-800 hover:bg-amber-50"
              >
                <Calculator className="w-3.5 h-3.5 mr-1" /> Calculate
              </Button>
            ) : <div />}
          </div>
        </div>
        {f.enable_remarks && <RemarkInput fieldId={f.field_id} fieldRemarks={fieldRemarks} updateRemark={updateRemark} dataTestId={`quote-remark-${f.field_id}`} />}
        </React.Fragment>
      );
    } else if (!seenGroups.has(group)) {
      seenGroups.add(group);
      const groupFields = inputFields.filter(gf => gf.group?.trim() === group);
      const numberField = groupFields.find(gf => gf.type === 'number');
      const dropdownField = groupFields.find(gf => gf.type === 'dropdown');
      const numVal = numberField ? parseFloat(fieldValues[numberField.field_id]) || 0 : 1;
      const disableDropdown = numVal === 0;
      const missingBrand = numVal > 0 && dropdownField && !fieldValues[dropdownField.field_id];
      const groupRemarks = groupFields.map(gf => gf.remark).filter(Boolean);
      const groupConflict = dropdownField && conflictFieldIds.has(dropdownField.field_id) && conflictMap[dropdownField.field_id];
      const remarkOwner = groupFields.find(gf => gf.enable_remarks);

      rendered.push(
        <React.Fragment key={`group-${group}`}>
        <div data-testid={`quote-group-${group.replace(/\s+/g, '-').toLowerCase()}`}
          className={`grid grid-cols-[1fr_auto_auto] items-center gap-3 px-5 py-3.5 ${remarkOwner ? '' : 'border-b border-stone-100 last:border-b-0'} ${groupConflict ? 'bg-red-50/50' : missingBrand ? 'bg-amber-50/40' : ''}`}>
          <div className="min-w-0">
            <div className="flex items-center">
              <span className="text-sm font-medium text-stone-800">{group}</span>
              {groupRemarks.length > 0 && <RemarkDisplay remark={groupRemarks.join('. ')} />}
              {!groupConflict && missingBrand && <span className="text-[10px] text-amber-700 ml-2 font-medium">Select a brand</span>}
            </div>
            {groupConflict && (
              <p className="text-[10px] text-red-600 mt-0.5 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                <span className="line-through">{groupConflict.oldValue}</span>
                <span>is no longer available</span>
              </p>
            )}
            {!groupConflict && groupRemarks.length > 0 && <p className="text-[10px] text-stone-400 mt-0.5">{groupRemarks.join('. ')}</p>}
          </div>
          {/* Number slot */}
          <div className="w-28">
            {numberField ? (
              <Input
                data-testid={`quote-input-${numberField.field_id}`}
                type="number" step="any"
                value={fieldValues[numberField.field_id] ?? ''}
                onChange={e => updateField(numberField.field_id, e.target.value)}
                placeholder="0"
                className="w-full border-stone-200 text-sm text-right tabular-nums"
              />
            ) : <div />}
          </div>
          {/* Brand slot */}
          <div className="w-48">
            {dropdownField ? (
              <Select value={fieldValues[dropdownField.field_id] || ''} onValueChange={v => updateField(dropdownField.field_id, v)} disabled={disableDropdown}>
                <SelectTrigger data-testid={`quote-select-${dropdownField.field_id}`} className={`w-full text-sm ${groupConflict ? 'border-red-300 bg-red-50' : 'border-stone-200'} ${disableDropdown ? 'opacity-40' : ''}`}>
                  <SelectValue placeholder="Select brand..." />
                </SelectTrigger>
                <SelectContent>
                  {dropdownField.options?.map(o => <SelectItem key={o.label} value={o.label}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : <div />}
          </div>
        </div>
        {remarkOwner && <RemarkInput fieldId={remarkOwner.field_id} fieldRemarks={fieldRemarks} updateRemark={updateRemark} dataTestId={`quote-remark-${remarkOwner.field_id}`} />}
        </React.Fragment>
      );
    }
  }

  return <>{rendered}</>;
}
